import React, { useState, useEffect, useRef, useContext } from 'react';
import './ParkAssistActive.scss';
import VideoSimple from '@/v2/components/media/video/VideoSimple';
import Header from '@/v2/components/application/Header';
import TimelineText from '@/v2/components/atoms/TimelineText';
import TimeController from '@/v2/components/controllers/TimeController';
import Button from '@/v2/components/atoms/Button';
import TextBottom from '@/v2/components/atoms/TextBottom';
import Fade from '@/v2/components/atoms/Fade';
import Disclaimer from '@/v2/components/layouts/Disclaimer';
import { DriverAssistContext } from '@/v2/state/providers';
import { MomentStateContext } from '@/v2/state/momentState';
import { AnalyticsContext } from '@/v2/components/application/Analytics';
import { ClearMemory } from '@/v2/components/utils/DeviceUtils';
import { timeout, saveTimeout, clearTimeouts } from '@/v2/components/utils/Timeout';

let videoController = null;

const FADE_TIME = 1000;

const STATE = {
    none: 'none',
    intro: 'intro',
    cta: 'cta',
    videoPlaying: 'videoPlaying',
    videoFinished: 'videoFinished',
    timeline: 'timeline',
    fade: 'fade',
    end: 'end',
};

const videoReady = (controller, onReady) => {
    // eslint-disable-next-line no-param-reassign
    videoController = controller;
    onReady();
};

const stateIntro = (timeController, setTime, timeline, cb) => {
    // eslint-disable-next-line no-param-reassign
    timeController.current = new TimeController(setTime);
    saveTimeout(timeController.current.AddTimeoutFromTimeline(timeline, () => cb()));
};

const stateVideoPlaying = () => {
    videoController.Play();
};

const stateTimeline = (timeController, timeline, cb) => {
    saveTimeout(timeController.current.AddTimeoutFromTimeline(timeline, () => cb()));
};

export default ({ isActive, data, onFinished, onReady }) => {
    const [state, setState] = useState(STATE.none);
    const [btnAnimate, setBtnAnimate] = useState('in');
    const [textBottomAnimate, setTextBottomAnimate] = useState('out');

    const timeController = useRef(null);
    const time = useRef(TimelineText.Time());

    const [{ disclaimer }] = useContext(DriverAssistContext);
    const { dispatch: momentDispatch } = useContext(MomentStateContext);
    const { trackEvent } = useContext(AnalyticsContext);

    const stateFade = (cb) => {
        timeout(() => cb(), FADE_TIME);
    };

    const handleClick = () => {
        setBtnAnimate('out');
        timeout(() => {
            setState(STATE.videoPlaying);
        }, 1000);
        trackEvent('vcClick', {
            vcStep: 'parkAssistActive',
            vcClick: 'button:activate parking',
        });
    };

    useEffect(() => () => clearTimeouts(), []);

    useEffect(() => {
        if (isActive) {
            setState(STATE.intro);
        }
    }, [isActive]);

    useEffect(() => {
        if (isActive && videoController) {
            videoController.ProgressDispatch(momentDispatch);
        }
    }, [isActive, videoController]);

    useEffect(() => {
        switch (state) {
            case STATE.intro:
                stateIntro(timeController, time.current.Set, data.text.timeline, () => setState(STATE.cta));
                break;
            case STATE.videoPlaying:
                stateVideoPlaying();
                setTextBottomAnimate('in');
                timeout(() => {
                    setTextBottomAnimate('out');
                }, 5000);

                timeout(() => {
                    setState(STATE.videoFinished);
                    setTextBottomAnimate('in');
                }, 6300);
                break;
            case STATE.videoFinished:
                timeout(() => {
                    setTextBottomAnimate('out');
                }, 1700);

                timeout(() => {
                    setState(STATE.timeline);
                }, 3000);
                break;
            case STATE.timeline:
                stateTimeline(timeController, data.text.secondaryTimeline, () => setState(STATE.fade));
                break;
            case STATE.fade:
                stateFade(() => setState(STATE.end));
                break;
            case STATE.end:
                onFinished();
                ClearMemory();
                break;
            default:
        }
    }, [state]);

    const options = {
        bufferBeforePlay: true,
        onReady: (controller) => videoReady(controller, onReady),
        autoPlay: false,
    };

    const timeline = state === STATE.timeline ? data.text.secondaryTimeline : data.text.timeline;
    const textBottom = state === STATE.videoPlaying ? data.text.text : data.text.secondaryText;
    const disclaimerContent = disclaimer ? <Disclaimer data={disclaimer} dark /> : '';

    return (
        <div className="ParkAssistActive">
            <VideoSimple src={data.video} options={options} />
            <Header show hideLogo dark />
            <TimelineText data={timeline} time={time.current} key={`timeline-${state}`} background="dark" color="black" />
            {state === STATE.cta && (
                <div className="ParkAssistActive__cta">
                    <Button variant="base" animate={btnAnimate} onClick={handleClick}>
                        {data.text.cta.continue}
                    </Button>
                </div>
            )}
            <TextBottom background="dark" animate={textBottomAnimate} color="nim--blue" component="h4">
                {textBottom}
            </TextBottom>
            <Fade show={!isActive || state === STATE.fade || state === STATE.end} light cover />
            {disclaimerContent}
        </div>
    );
};
